import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@material-ui/core';
import { ExitToAppRounded as ExitToAppIcon, PersonAddRounded as PersonAddIcon } from '@material-ui/icons';
import Skeleton from '@material-ui/lab/Skeleton';

import { useAdminGetUsers } from '../../hooks/admin-get-users.hook';
import { useDebounce } from '../../../shared/hooks/use-debounce.hook';
import { User } from '../../types/user.type';
import { ADMIN_USERS_APP_PATHS } from '../../constants/users-paths.constants';
import { UserRoleChip } from '../Chips/UserRole.chip';

type UserSearchTableProps = {
  selectedMemberIds: string[];
  onAddMember: (user: User) => void;
  maxHeight?: string;
};

const useStyles = makeStyles({
  search: {
    marginBottom: '0.5rem',
  },
});

function UserTableRow({
  user,
  isSelected,
  onAddMember,
}: {
  user: User;
  isSelected: boolean;
  onAddMember: (user: User) => void;
}) {
  return (
    <TableRow>
      <TableCell>
        <IconButton
          size="small"
          aria-label="voir"
          component={Link}
          to={ADMIN_USERS_APP_PATHS.USER_DETAILS(user.id)}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ExitToAppIcon />
        </IconButton>
      </TableCell>
      <TableCell>
        <UserRoleChip role={user.role} size="small" />
      </TableCell>
      <TableCell>
        {user.firstname} {user.lastname}
      </TableCell>
      <TableCell>{user.email}</TableCell>
      <TableCell>
        <IconButton size="small" onClick={() => onAddMember(user)} disabled={isSelected}>
          <PersonAddIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

export function UserSearchTable({ selectedMemberIds, onAddMember, maxHeight = '40svh' }: UserSearchTableProps) {
  const classes = useStyles();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const { isLoading, isRefetching, users } = useAdminGetUsers({
    page: 1,
    limit: 10,
    sortBy: 'lastname',
    sortDirection: 'asc',
    filters: { search: debouncedSearch || undefined },
  });

  const isFetching = isLoading || isRefetching;
  const hasUsers = !!users.length;

  return (
    <>
      <TextField
        className={classes.search}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        size="small"
        label="Rechercher un utilisateur"
        placeholder="Email ou nom complet"
        variant="outlined"
      />
      <Paper>
        <TableContainer style={{ minHeight: maxHeight, maxHeight }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell style={{ fontWeight: 'bold' }}>Rôle</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Nom complet</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Email</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {isFetching && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Skeleton variant="rect" animation="wave" height={50} />
                  </TableCell>
                </TableRow>
              )}
              {!isFetching && !hasUsers && (
                <TableRow>
                  <TableCell colSpan={5}>Aucun utilisateur</TableCell>
                </TableRow>
              )}
              {!isFetching &&
                hasUsers &&
                users.map((user) => (
                  <UserTableRow
                    key={user.id}
                    user={user}
                    isSelected={selectedMemberIds.includes(user.id)}
                    onAddMember={onAddMember}
                  />
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
}
